/**
 * Account → Change password (SPEC decision 38, plan P40). Three fields validated in place, then Cognito's ChangePassword
 * with the session's access token (auth/changePassword.ts): Hangar's API never sees a password. Offered only when the
 * console signs in through a Cognito user pool.
 */
import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { changePassword } from '@/auth/changePassword';
import { validatePasswordChange, type PasswordChangeErrors, type PasswordChangeFailure, type PasswordChangeInput } from '@/auth/passwordModel';
import { FormField, fieldA11y } from '@/components/FormField';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

const EMPTY: PasswordChangeInput = { current: '', next: '', confirm: '' };
const FIELDS: { key: keyof PasswordChangeInput; label: string; autoComplete: string; hint?: string }[] = [
  { key: 'current', label: 'Current password', autoComplete: 'current-password' },
  { key: 'next', label: 'New password', autoComplete: 'new-password', hint: 'At least 12 characters, with an uppercase letter, a lowercase letter and a number.' },
  { key: 'confirm', label: 'Repeat the new password', autoComplete: 'new-password' },
];

function useChangePasswordForm(onDone: () => void) {
  const [values, setValues] = useState<PasswordChangeInput>(EMPTY);
  const [errors, setErrors] = useState<PasswordChangeErrors>({});
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (key: keyof PasswordChangeInput, value: string) => {
    setValues((v) => ({ ...v, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };
  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const found = validatePasswordChange(values);
    setErrors(found);
    setProblem(null);
    if (Object.keys(found).length) return;
    setBusy(true);
    try {
      await changePassword(values.current, values.next);
      toast.success('Password changed', { description: 'Use the new password the next time you sign in.' });
      onDone();
    } catch (err) {
      const f = err as PasswordChangeFailure;
      if (f.field) setErrors({ [f.field]: f.message });
      else setProblem(f.message ?? 'Could not change the password. Try again.');
    } finally {
      setBusy(false);
    }
  };
  return { values, errors, problem, busy, set, onSubmit };
}

function ChangePasswordForm({ onDone }: { onDone: () => void }) {
  const f = useChangePasswordForm(onDone);
  return (
    <form data-testid="change-password-form" onSubmit={(e) => void f.onSubmit(e)} noValidate className="flex min-h-0 flex-col" aria-label="Change your password">
      <DialogHeader>
        <DialogTitle>Change password</DialogTitle>
        <DialogDescription id="change-password-desc">Your sign-in password for Hangar. It goes straight to the sign-in service; Hangar never stores or sees it.</DialogDescription>
      </DialogHeader>
      <DialogBody className="flex flex-col gap-4">
        {FIELDS.map(({ key, label, autoComplete, hint }) => (
          <FormField key={key} id={`change-password-${key}`} label={label} required hint={hint} error={f.errors[key]}>
            <Input data-testid={`change-password-${key}`} type="password" value={f.values[key]} onChange={(e) => f.set(key, e.target.value)} autoComplete={autoComplete} spellCheck={false} {...fieldA11y(`change-password-${key}`, f.errors[key], hint ? 'hint' : undefined)} />
          </FormField>
        ))}
        {f.problem ? (
          <Alert variant="destructive" data-testid="change-password-problem">
            <AlertDescription>{f.problem}</AlertDescription>
          </Alert>
        ) : null}
      </DialogBody>
      <DialogFooter>
        <Button variant="ghost" onClick={onDone}>
          Cancel
        </Button>
        <Button type="submit" data-testid="change-password-submit" loading={f.busy}>
          Change password
        </Button>
      </DialogFooter>
    </form>
  );
}

export function ChangePasswordDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="change-password-dialog" aria-describedby="change-password-desc">
        {open ? <ChangePasswordForm onDone={() => onOpenChange(false)} /> : null}
      </DialogContent>
    </Dialog>
  );
}
